import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TinsaAuthService } from "../services/auth.service";
import PropertyDetailsService from "../services/property.details.service";
import { UserProfileService } from "../services/user.profile.service";
import { NotificationService } from "../services/notifications.service";
import { LocalizationService } from "../services/localization.service";
import MortgageService from "../services/mortgage.service";

export const getPhoneNumberAccessToken = createAsyncThunk(
  "getPhoneNumberAccessToken",
  async (
    { valuationId, phoneNumber, smsCode },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await TinsaAuthService.validateSMSCode(
        valuationId,
        phoneNumber,
        smsCode
      );
      return fulfillWithValue({
        authToken: response.data.token,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getEmailAccessToken = createAsyncThunk(
  "getEmailAccessToken",
  async (
    { valuationId, socialToken, provider, redirectUri },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await TinsaAuthService.validateEmail(
        valuationId,
        socialToken,
        provider,
        redirectUri
      );
      return fulfillWithValue({
        authToken: response.data.token,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message,
        authProvider: provider
      });
    }
  }
);

export const getUserSMSCode = createAsyncThunk(
  "getUserSMSCode",
  async (
    { valuationId, phoneNumber },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      await TinsaAuthService.sendSMSCode(valuationId, phoneNumber);
      return fulfillWithValue(phoneNumber);
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getBasicValuationReport = createAsyncThunk(
  "getBasicValuationReport",
  async (valuationId, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await PropertyDetailsService.getBasicValuationReport(
        valuationId
      );
      return fulfillWithValue({
        report: response.data,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getValuationReport = createAsyncThunk(
  "getValuationReport",
  async ({ valuationId, authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const [valuationResponse, userValuationResponse] = await Promise.all([
        PropertyDetailsService.getValuationReport(authToken),
        PropertyDetailsService.getUserValuationReport(authToken)
      ]);
      const response = {
        ...userValuationResponse.data,
        ...valuationResponse.data
      };
      return fulfillWithValue({
        report: response,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getUserValuationReport = createAsyncThunk(
  "getUserValuationReport",
  async ({ valuationId, authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await PropertyDetailsService.getUserValuationReport(
        authToken
      );
      return fulfillWithValue({
        report: response.data,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getWidgetInfo = createAsyncThunk(
  "getWidgetInfo",
  async ({ authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await LocalizationService.getWidgetInfo(authToken);
      return fulfillWithValue({
        widgetInfo: response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getUserValuations = createAsyncThunk(
  "getUserValuations",
  async ({ authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await UserProfileService.getUserValuations(authToken);
      return fulfillWithValue({ valuations: response.data });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getValuationFiles = createAsyncThunk(
  "getValuationFiles",
  async ({ authToken, valuationId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await PropertyDetailsService.getValuationFiles(
        authToken,
        valuationId
      );
      return fulfillWithValue({
        files: response.data,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getValuationWarnings = createAsyncThunk(
  "getValuationWarnings",
  async (
    { valuationId, authToken, codes },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await PropertyDetailsService.getValuationWarnings(
        authToken,
        codes
      );
      return fulfillWithValue({
        warnings: response.data,
        valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getValuationImages = createAsyncThunk(
  "getValuationImages",
  async ({ valuationId, authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await PropertyDetailsService.getValuationImages(
        authToken,
        valuationId
      );

      return fulfillWithValue({
        images: response.data.images,
        image: response.data.images,
        valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const selectValuation = createAsyncThunk(
  "selectValuation",
  async ({ authToken, valuationId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await TinsaAuthService.setTokenValuation(
        authToken,
        valuationId
      );

      return fulfillWithValue({
        token: response.data.token,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getUserProfile = createAsyncThunk(
  "getUserProfile",
  async ({ valuationId, authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await UserProfileService.getUserProfile(authToken);
      return fulfillWithValue({
        valuationId: valuationId,
        profile: response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "updateUserProfile",
  async (
    { valuationId, authToken, profile },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await UserProfileService.updateUserProfile(
        authToken,
        profile
      );
      return fulfillWithValue({
        valuationId: valuationId,
        profile: response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getUserNotifPreferences = createAsyncThunk(
  "getUserNotifPreferences",
  async ({ valuationId, authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await NotificationService.getUserNotifPreferences(
        authToken
      );
      return fulfillWithValue({
        valuationId: valuationId,
        preferences: response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const updateUserNotifPreferences = createAsyncThunk(
  "updateUserNotifPreferences",
  async (
    { valuationId, authToken, preferences },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await NotificationService.updateUserNotifPreferences(
        authToken,
        preferences
      );
      return fulfillWithValue({
        valuationId: valuationId,
        preferences: response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getValuationPoi = createAsyncThunk(
  "getValuationPoi",
  async (
    { valuationId, poi, authToken },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await PropertyDetailsService.getValuationPoi(authToken, {
        lat: poi.coordinates[1],
        long: poi.coordinates[0],
        name: poi.name
      });

      return fulfillWithValue({
        valuationId: valuationId,
        poi: {
          placesData: response.data,
          tinsaData: poi
        }
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const getMortgageInfo = createAsyncThunk(
  "getMortgageInfo",
  async ({ authToken }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await MortgageService.getValuationMortgage(authToken);
      return fulfillWithValue({
        ...response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const createMortgageInfo = createAsyncThunk(
  "createMortgageInfo",
  async (
    {
      type,
      rate,
      provider,
      currentValue,
      latestEstimatedOfferPrice,
      authToken
    },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await MortgageService.createValuationMortgage(
        authToken,
        { type, rate, provider, currentValue, latestEstimatedOfferPrice }
      );
      return fulfillWithValue({
        ...response.data
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

export const updateValuationField = createAsyncThunk(
  "updateValuationField",
  async (
    { authToken, valuationId, userValuationId, data },
    { rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await PropertyDetailsService.updateValuationField(
        authToken,
        userValuationId,
        data
      );
      return fulfillWithValue({
        data: response.data,
        valuationId: valuationId
      });
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        message: err.message
      });
    }
  }
);

const valuationsDetailsSlice = createSlice({
  name: "valuationsDetails",
  initialState: {
    independentAuthToken: null,
    valuations: {},
    currentMortgage: {
      type: "fixed",
      rate: 0,
      provider: "",
      currentValue: 0
    },
    previousMortgage: {
      type: "fixed",
      rate: 0,
      provider: "",
      currentValue: 0
    },
    isLoading: false,
    isLoginLoading: false,
    profileLoading: false,
    valuationLoading: false,
    valuationsLoading: false,
    imagesLoading: false,
    warningsLoading: false,
    poiLoading: false,
    mortgageLoading: false,
    currentValuation: null,
    getReportError: false,
    getBasicError: false,
    widgetInfo: {},
    successMessage: "",
    errorMessage: "",
    errorCode: null
  },
  reducers: {
    clearMessages: (state, action) => {
      state.errorMessage = "";
      state.successMessage = "";
      state.errorCode = null;
    },
    setCurrentValuation: (state, action) => {
      state.currentValuation = action.payload;
    },
    setValuations: (state, action) => {
      state.valuations = action.payload?.valuations;
    },
    setFacebookLoginError: (state, action) => {
      state.errorMessage = "toasts.login-error";
    },
    setGoogleLoginError: (state, action) => {
      state.errorMessage = "toasts.login-error";
    },
    signOut: (state, action) => {
      state.valuations[action.payload?.valuationId] = {
        ...state.valuations[action.payload?.valuationId],
        authToken: undefined
      };
    },
    signOutAllValuations: (state, action) => {
      Object.keys(state.valuations).forEach((key) => {
        state.valuations[key].authToken = undefined;
      });
    },
    signOutIndependentToken: (state, action) => {
      state.independentAuthToken = null;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload?.isLoading;
    },
    setIsLoginLoading: (state, action) => {
      state.isLoginLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmailAccessToken.pending, (state, action) => {
        state.isLoginLoading = true;
        state.errorMessage = "";
        state.successMessage = "";
      })
      .addCase(getEmailAccessToken.fulfilled, (state, action) => {
        state.isLoginLoading = false;

        if (action.payload?.valuationId) {
          state.valuations[action.payload?.valuationId] = {
            ...state.valuations[action.payload?.valuationId],
            authToken: action.payload?.authToken
          };
          state.independentAuthToken = null;
        } else {
          state.independentAuthToken = action.payload?.authToken;
        }

        state.successMessage = "toasts.login-success";
      })
      .addCase(getEmailAccessToken.rejected, (state, action) => {
        state.isLoginLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          authToken: undefined
        };
        state.independentAuthToken = null;
        state.authProvider = action.payload?.authProvider;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getPhoneNumberAccessToken.pending, (state, action) => {
        state.isLoginLoading = true;
        state.errorMessage = "";
        state.successMessage = "";
      })
      .addCase(getPhoneNumberAccessToken.fulfilled, (state, action) => {
        state.isLoginLoading = false;
        if (action.payload?.valuationId) {
          state.valuations[action.payload?.valuationId] = {
            ...state.valuations[action.payload?.valuationId],
            authToken: action.payload?.authToken
          };
          state.independentAuthToken = null;
        } else {
          state.independentAuthToken = action.payload?.authToken;
        }
        state.successMessage = "toasts.login-success";
      })
      .addCase(getPhoneNumberAccessToken.rejected, (state, action) => {
        state.isLoginLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          authToken: undefined
        };
        state.independentAuthToken = null;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getBasicValuationReport.pending, (state, action) => {
        state.isLoading = true;
        state.getBasicError = false;
      })
      .addCase(getBasicValuationReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          basicValuation: { ...action.payload?.report }
        };
        state.getBasicError = false;
      })
      .addCase(getBasicValuationReport.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
        state.getBasicError = true;
      })
      .addCase(getValuationReport.pending, (state, action) => {
        state.valuationLoading = true;
        state.getReportError = false;
      })
      .addCase(getValuationReport.fulfilled, (state, action) => {
        state.valuationLoading = false;
        state.valuations[action.payload?.valuationId].valuation = {
          ...state.valuations[action.payload?.valuationId].valuation,
          ...action?.payload?.report
        };
        state.currentValuation = action.payload?.valuationId;
        state.getReportError = false;
      })
      .addCase(getValuationReport.rejected, (state, action) => {
        state.valuationLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
        state.getReportError = true;
      })

      .addCase(getUserSMSCode.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getUserSMSCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.phoneNumber = action.payload;
        state.successMessage = "toasts.otp-sent-success";
      })
      .addCase(getUserSMSCode.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getUserProfile.pending, (state, action) => {
        state.profileLoading = true;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          userProfile: action.payload?.profile
        };
      })
      .addCase(getWidgetInfo.pending, (state, action) => {
        state.widgetInfoLoading = true;
      })
      .addCase(getWidgetInfo.rejected, (state, action) => {
        state.widgetInfoLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getWidgetInfo.fulfilled, (state, action) => {
        state.widgetInfoLoading = false;
        state.widgetInfo = action.payload?.widgetInfo;
      })
      .addCase(getUserValuations.pending, (state, action) => {
        state.valuationsLoading = true;
      })
      .addCase(getUserValuations.rejected, (state, action) => {
        state.valuationsLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getUserValuations.fulfilled, (state, action) => {
        state.valuationsLoading = false;
        state.valuations = state.valuations ?? {};
        action.payload?.valuations.forEach((valuation) => {
          if (!state.valuations.hasOwnProperty(valuation.id))
            state.valuations[valuation.id] = {
              basicValuation: { ...valuation }
            };
        });
      })
      .addCase(getValuationFiles.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getValuationFiles.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getValuationFiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.valuations[action.payload?.valuationId].basicValuation.files =
          action.payload?.files;
      })
      .addCase(getValuationWarnings.pending, (state, action) => {
        state.warningsLoading = true;
      })
      .addCase(getValuationWarnings.rejected, (state, action) => {
        state.warningsLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getValuationWarnings.fulfilled, (state, action) => {
        state.warningsLoading = false;
        state.valuations[action.payload?.valuationId].basicValuation.warnings =
          action.payload?.warnings;
      })
      .addCase(getValuationImages.pending, (state, action) => {
        state.imagesLoading = true;
      })
      .addCase(getValuationImages.rejected, (state, action) => {
        state.imagesLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getValuationImages.fulfilled, (state, action) => {
        state.imagesLoading = false;
        state.valuations[action.payload?.valuationId].basicValuation.images =
          action.payload?.images;
        state.valuations[action.payload?.valuationId].basicValuation.image =
          action.payload?.images?.front_image?.[0] || null;
      })
      .addCase(selectValuation.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(selectValuation.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
        state.currentValuation = null;
      })
      .addCase(selectValuation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          authToken: action.payload?.token
        };
        state.independentAuthToken = null;
        state.successMessage = "toasts.home-selected";
        state.currentValuation = action.payload?.valuationId;
      })
      .addCase(updateUserProfile.pending, (state, action) => {
        state.profileLoading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.successMessage = "toasts.edit-profile-success";
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          userProfile: action.payload?.profile
        };
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getUserNotifPreferences.pending, (state, action) => {
        state.profileLoading = true;
      })
      .addCase(getUserNotifPreferences.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          userProfile: {
            ...state.valuations[action.payload?.valuationId]?.userProfile,
            preferences: {
              emailNotifications: action.payload?.preferences.email,
              smsNotifications: action.payload?.preferences.sms,
              promotionalNotifications: action.payload?.preferences.promotional,
              pushNotifications: action.payload?.preferences.push
            }
          }
        };
      })
      .addCase(getUserNotifPreferences.rejected, (state, action) => {
        state.profileLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(updateUserNotifPreferences.pending, (state, action) => {
        state.profileLoading = true;
      })
      .addCase(updateUserNotifPreferences.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.valuations[action.payload?.valuationId] = {
          ...state.valuations[action.payload?.valuationId],
          userProfile: {
            ...state.valuations[action.payload?.valuationId]?.userProfile,
            preferences: {
              emailNotifications: action.payload?.preferences.email,
              smsNotifications: action.payload?.preferences.sms,
              promotionalNotifications: action.payload?.preferences.promotional,
              pushNotifications: action.payload?.preferences.push
            }
          }
        };
        state.successMessage = "toasts.notifications-successfully-edited";
      })
      .addCase(updateUserNotifPreferences.rejected, (state, action) => {
        state.profileLoading = false;
        state.errorCode = action.payload?.status;
        state.errorMessage = action.payload?.message;
      })
      .addCase(getValuationPoi.pending, (state) => {
        state.poiLoading = true;
      })
      .addCase(getValuationPoi.fulfilled, (state, action) => {
        state.poiLoading = false;
        state.valuations[action.payload.valuationId].basicValuation = {
          ...state.valuations[action.payload.valuationId].basicValuation,
          pois: {
            ...state.valuations[action.payload.valuationId].basicValuation.pois,
            [`${action.payload.poi.tinsaData.coordinates[0]}|${action.payload.poi.tinsaData.coordinates[1]}`]:
              {
                placesData: action.payload.poi.placesData.data,
                tinsaData: action.payload.poi.tinsaData
              }
          }
        };
      })
      .addCase(getValuationPoi.rejected, (state, action) => {
        state.poiLoading = false;
        state.errorCode = action.payload.status;
        state.errorMessage = action.payload.message;
      })
      .addCase(getMortgageInfo.pending, (state) => {
        state.successMessage = "";
        state.mortgageLoading = true;
      })
      .addCase(getMortgageInfo.fulfilled, (state, action) => {
        state.mortgageLoading = false;
        state.currentMortgage = { ...action.payload.currentMortgage };
        state.previousMortgage = { ...action.payload.previousMortgage };
      })
      .addCase(getMortgageInfo.rejected, (state, action) => {
        state.poiLoading = false;
        state.errorCode = action.payload.status;
        state.errorMessage = action.payload.message;
      })
      .addCase(createMortgageInfo.pending, (state) => {
        state.mortgageLoading = true;
      })
      .addCase(createMortgageInfo.fulfilled, (state, action) => {
        state.mortgageLoading = false;
        state.successMessage = "toasts.mortgage-success";
        state.currentMortgage = { ...action.payload };
      })
      .addCase(createMortgageInfo.rejected, (state, action) => {
        state.mortgageLoading = false;
        state.errorCode = action.payload.status;
        state.errorMessage = action.payload.message;
      })
      .addCase(updateValuationField.pending, (state, action) => {
        state.valuationLoading = true;
      })
      .addCase(updateValuationField.fulfilled, (state, action) => {
        state.valuationLoading = false;
        state.successMessage = "toasts.valuation-update-success";
        state.valuations[action.payload?.valuationId].valuation = {
          ...state.valuations[action.payload?.valuationId].valuation,
          ...action?.payload?.data
        };
      })
      .addCase(updateValuationField.rejected, (state, action) => {
        state.valuationLoading = false;
        state.errorCode = action.payload.status;
        state.errorMessage = action.payload.message;
      });
  }
});

export const {
  signOut,
  signOutAllValuations,
  signOutIndependentToken,
  clearMessages,
  setCurrentValuation,
  clearPhoneNumber,
  setFacebookLoginError,
  setGoogleLoginError,
  setIsLoading,
  setValuations,
  setIsLoginLoading
} = valuationsDetailsSlice.actions;

export default valuationsDetailsSlice.reducer;
